import {
  type CSSProperties,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { cn } from "../../utils";
import { Tooltip } from "../Tooltip";
import type { TooltipProps } from "../Tooltip/types";
import type { TruncatedContentProps } from "./types";

const CLAMP_EPSILON = 0.5;

export const TruncatedContent = ({
  children,
  maxLines = 1,
  as,
  className,
  style: outerStyle = {},
  tooltipProps,
  tooltipClassName,
  tooltipSide,
}: TruncatedContentProps) => {
  const ref = useRef<HTMLElement | null>(null);
  const [isClamped, setIsClamped] = useState(false);
  const Tag = as || "div";

  const style = useMemo<CSSProperties>(() => {
    if (maxLines <= 1) {
      return {
        display: "block",
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        ...outerStyle,
      };
    }
    return {
      overflow: "hidden",
      lineClamp: String(maxLines),
      display: "-webkit-box",
      WebkitBoxOrient: "vertical",
      WebkitLineClamp: String(maxLines),
      ...outerStyle,
    };
  }, [maxLines, outerStyle]);

  const measure = () => {
    const el = ref.current;
    if (!el) return;

    if (maxLines <= 1) {
      const clamped = el.scrollWidth - el.clientWidth > CLAMP_EPSILON;
      setIsClamped(clamped);
    } else {
      const clamped = el.scrollHeight - el.clientHeight > CLAMP_EPSILON;
      setIsClamped(clamped);
    }
  };

  useLayoutEffect(() => {
    measure();
  }, [children, maxLines]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const mergedTooltipProps: Partial<TooltipProps> = {
    placement: tooltipProps?.placement ?? tooltipSide,
    contentClassName: tooltipProps?.contentClassName ?? tooltipClassName,
    ...tooltipProps,
  };

  const trigger = (
    <Tag
      ref={ref}
      aria-label={
        isClamped && typeof children === "string" ? children : undefined
      }
      className={cn("overflow-hidden", className)}
      style={style}
    >
      {children}
    </Tag>
  );

  return isClamped ? (
    <Tooltip {...mergedTooltipProps} content={children}>
      {trigger}
    </Tooltip>
  ) : (
    trigger
  );
};
